
import { useState, FormEvent } from 'react';

const FORM_URL = 'https://readdy.ai/api/form/d6a2to6a728k8ctu3120';

export default function ContactSection() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    const form = e.currentTarget;
    const formData = new FormData(form);

    const name = formData.get('name') as string;
    const email = formData.get('email') as string;
    const movingFrom = formData.get('moving_from') as string;
    const timeline = formData.get('timeline') as string;
    const message = formData.get('message') as string;

    if (!name || !email || !movingFrom || !timeline) {
      setError('Please fill in all required fields.');
      return;
    }

    if (message && message.length > 500) {
      setError('Message must be 500 characters or less.');
      return;
    }

    setIsSubmitting(true);

    try {
      const body = new URLSearchParams();
      body.append('name', name);
      body.append('email', email);
      body.append('moving_from', movingFrom);
      body.append('timeline', timeline);
      if (message) body.append('message', message);

      await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      setIsSubmitted(true);
      form.reset();
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-20 px-6 scroll-mt-32">
      <div className="max-w-6xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div>
            <span className="inline-flex items-center gap-2 text-teal-700 bg-teal-50 text-sm font-medium px-4 py-1.5 rounded-full mb-4">
              <i className="ri-chat-smile-2-line"></i>
              We&apos;re Here to Help
            </span>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Need Personalized Relocation Help?</h2>
            <p className="text-gray-500 text-sm leading-relaxed mb-8">
              Moving to a new city can be overwhelming. Fill out the form and our community team will send you a personalized welcome packet with resources tailored to your needs.
            </p>

            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-teal-50">
                  <i className="ri-phone-line text-teal-600"></i>
                </div>
                <div>
                  <p className="text-xs text-gray-400">Call Us</p>
                  <a href="tel:+19162388515" className="text-sm font-medium text-gray-900 hover:text-teal-600 cursor-pointer">(916) 238-8515</a>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-teal-50">
                  <i className="ri-mail-line text-teal-600"></i>
                </div>
                <div>
                  <p className="text-xs text-gray-400">Email</p>
                  <a href="mailto:welcome@roseville.ca.us" className="text-sm font-medium text-gray-900 hover:text-teal-600 cursor-pointer">welcome@roseville.ca.us</a>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-teal-50">
                  <i className="ri-map-pin-line text-teal-600"></i>
                </div>
                <div>
                  <p className="text-xs text-gray-400">Visit Us</p>
                  <p className="text-sm font-medium text-gray-900">5098 Foothills Blvd, Suite 3-402, Roseville, CA 95747</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Form */}
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6">
            {isSubmitted ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 flex items-center justify-center rounded-full bg-emerald-100 mx-auto mb-4">
                  <i className="ri-check-line text-emerald-600 text-3xl"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Welcome Packet on Its Way!</h3>
                <p className="text-sm text-gray-500">We&apos;ll send your personalized relocation guide within 24 hours.</p>
              </div>
            ) : (
              <form
                id="relocation-contact-form"
                data-readdy-form
                onSubmit={handleSubmit}
              >
                <h3 className="text-lg font-bold text-gray-900 mb-5">Get Your Free Welcome Packet</h3>

                {error && (
                  <div className="bg-red-50 text-red-600 text-sm px-4 py-3 rounded-lg mb-4">
                    <i className="ri-error-warning-line mr-1.5"></i>
                    {error}
                  </div>
                )}

                <div className="space-y-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1.5">Full Name *</label>
                    <input
                      type="text"
                      name="name"
                      placeholder="Your full name"
                      className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1.5">Email Address *</label>
                    <input
                      type="email"
                      name="email"
                      placeholder="your@email.com"
                      className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1.5">Moving From *</label>
                    <input
                      type="text"
                      name="moving_from"
                      placeholder="City, State or Country"
                      className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1.5">Moving Timeline *</label>
                    <select
                      name="timeline"
                      className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent cursor-pointer"
                      required
                    >
                      <option value="">Select timeline</option>
                      <option value="Within 1 month">Within 1 month</option>
                      <option value="1-3 months">1-3 months</option>
                      <option value="3-6 months">3-6 months</option>
                      <option value="6-12 months">6-12 months</option>
                      <option value="Just researching">Just researching</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1.5">Questions or Interests</label>
                    <textarea
                      name="message"
                      placeholder="Tell us what you'd like to know about Roseville..."
                      rows={3}
                      maxLength={500}
                      className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent resize-none"
                    ></textarea>
                    <p className="text-xs text-gray-400 mt-1">Max 500 characters</p>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full mt-5 bg-teal-600 text-white text-sm font-medium py-3 rounded-lg hover:bg-teal-700 transition-colors cursor-pointer disabled:opacity-50 whitespace-nowrap"
                >
                  {isSubmitting ? 'Sending...' : 'Send Me the Welcome Packet'}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
